import React from "react";
import SEO from "../components/SEO";
import { PageHero, SectionHeading, FeatureCard, CTABand, CheckList, Icon } from "../components/ui-blocks";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { insuranceVerification as iv, images } from "../mock/mock";

export default function InsuranceVerification() {
  return (
    <>
      <SEO
        title="Insurance Verification Services — Reduce Denials & Get Paid Faster"
        description="VerifiAssist provides structured insurance eligibility and benefits verification with HIPAA-conscious workflows — reduce claim denials, cut front-desk overload and improve reimbursement speed."
        path="/insurance-verification"
      />
      <PageHero eyebrow={iv.hero.eyebrow} title={iv.hero.title} subtitle={iv.hero.subtitle} image={images[iv.hero.image]}>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Button asChild size="lg" className="bg-brand-600 hover:bg-brand-700"><Link to="/contact-us">Request a Free Workflow Review <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
        </div>
      </PageHero>

      <section className="container-x py-16 sm:py-20">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <SectionHeading eyebrow="Accurate. Consistent. Reliable." title="Verify eligibility & benefits before patients arrive" />
            <div className="mt-5 space-y-4">
              {iv.intro.map((p, i) => <p key={i} className="text-slate-600 leading-relaxed">{p}</p>)}
            </div>
          </div>
          <div className="overflow-hidden rounded-3xl shadow-xl">
            <img src={images.proLaptop} alt="Healthcare professional verifying insurance eligibility on a laptop" className="h-[380px] w-full object-cover" />
          </div>
        </div>
      </section>

      <section className="bg-slate-50">
        <div className="container-x py-16 sm:py-20">
          <SectionHeading center eyebrow="Key benefits" title="Why practices outsource verification to us" subtitle="A structured process that reduces errors, protects revenue and gives time back to your front desk." />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {iv.benefits.map((b) => <FeatureCard key={b.title} icon={b.icon} title={b.title} body={b.body} />)}
          </div>
        </div>
      </section>

      <section className="container-x py-16 sm:py-20">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div className="order-2 lg:order-1 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-navy text-brand-teal"><Icon name="ShieldCheck" className="h-6 w-6" /></div>
            <h3 className="mt-5 text-2xl font-bold text-brand-navy">Confidently outsource without risk</h3>
            <ul className="mt-6 grid gap-3">
              <CheckList items={[
                "HIPAA-conscious workflows on every verification",
                "Secure handling of calls, email and eFax",
                "Trained, authorized agents only",
                "Rigorous quality assurance reviews",
                "Transparent performance reporting",
              ]} />
            </ul>
          </div>
          <div className="order-1 lg:order-2">
            <SectionHeading eyebrow="Security & compliance" title={iv.security.title} />
            <div className="mt-5 space-y-4">
              {iv.security.body.map((p, i) => <p key={i} className="text-slate-600 leading-relaxed">{p}</p>)}
            </div>
            <Button asChild className="mt-6 bg-brand-600 hover:bg-brand-700"><Link to="/contact-us">Request a consultation</Link></Button>
          </div>
        </div>
      </section>

      <CTABand title="Request a free insurance workflow review" body="We'll look at your current verification process and show you where missed details, delays and denials may be impacting revenue." secondaryTo="/dental-orthodontics" secondaryLabel="For dental & ortho practices" />
    </>
  );
}
